'use client'

import React from 'react'
import styles from '../../PluginTabs.module.css'

/**
 * @uiComponent
 * @name Presence
 * @description The Presence component enables real-time workspace sharing and collaboration features.
 * It generates shareable URLs that include the current layout and widget configuration, allowing users
 * to share their exact workspace setup with others. Features include automatic clipboard copying and modal notifications.
 * @location Presence plugin in the trading terminal
 * @prop none - No external props, operates as a standalone sharing utility
 */

/**
 * @uiWorkflow
 * @name Share Workspace Layout with Others
 * @description How to create and share a link to your current workspace configuration
 * @platform presence
 * @step 1 Navigate to the Presence plugin [component: Presence] [action: navigate]
 * @step 2 Click the "Presence" button [component: Presence] [action: click]
 * @step 3 Wait for the shareable URL to be generated [component: Presence] [action: wait] [notes: Includes layout and widget map]
 * @step 4 Review the modal with sharing information [component: Presence] [action: observe] [notes: Shows generated URL]
 * @step 5 Note that URL is automatically copied to clipboard [component: Presence] [action: acknowledge] [notes: Ready to paste anywhere]
 * @step 6 Share the URL with collaborators [component: Presence] [action: distribute] [notes: Via email, chat, etc.]
 * @step 7 Close the modal when finished [component: Presence] [action: close] [notes: Click Close button or wait 10 seconds]
 * @step 8 Monitor when others access your shared workspace [component: Presence] [action: track]
 */

/**
 * @uiWorkflow
 * @name Access Shared Workspace from Others
 * @description How to view someone else's shared workspace configuration
 * @platform presence
 * @step 1 Receive a presence link from another user [component: Presence] [action: receive] [notes: URL contains layout data]
 * @step 2 Click or paste the shared URL [component: Presence] [action: navigate]
 * @step 3 Wait for the workspace to load [component: Presence] [action: load] [notes: Layout and widgets restored]
 * @step 4 Observe the shared layout configuration [component: Presence] [action: examine] [notes: Exact replica of sharer's setup]
 * @step 5 See real-time cursor movements [component: Presence] [action: track] [notes: Collaborative viewing experience]
 * @step 6 Interact with the shared workspace [component: Presence] [action: explore] [notes: View data and analysis]
 * @step 7 Create your own presence link if desired [component: Presence] [action: share-back]
 */

/**
 * @uiWorkflow
 * @name Manage Workspace Sharing Settings
 * @description How to control and customize workspace sharing features
 * @platform presence
 * @step 1 Ensure your workspace is configured as desired [component: Presence] [action: setup] [notes: Layout and widgets arranged]
 * @step 2 Check current symbol in URL [component: Presence] [action: verify] [notes: Symbol included in shared link]
 * @step 3 Generate presence link when ready [component: Presence] [action: create]
 * @step 4 Review the generated URL structure [component: Presence] [action: examine] [notes: Contains symbol, instance ID, layout data]
 * @step 5 Test the link in a new browser tab [component: Presence] [action: validate] [notes: Verify sharing works correctly]
 * @step 6 Share with intended collaborators [component: Presence] [action: distribute]
 * @step 7 Monitor workspace access and usage [component: Presence] [action: track]
 * @step 8 Generate new links as workspace changes [component: Presence] [action: refresh]
 */

export const Presence = () => {
  const handlePresenceClick = () => {
    // Get current layout and widget map from localStorage
    const layout = localStorage.getItem('myLayout');
    const widgetMap = localStorage.getItem('myWidgetMap');
    
    // Create a unique instance ID
    const instanceId = Date.now().toString(36);
    
    // Get the current symbol from the URL
    const pathParts = window.location.pathname.split('/');
    const symbol = pathParts[1] || 'BTCUSDT';
    
    // Create a shareable URL with layout and widget map as query parameters
    const shareableUrl = `${window.location.origin}/${symbol}/${instanceId}?layout=${encodeURIComponent(layout || '')}&widgetMap=${encodeURIComponent(widgetMap || '')}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareableUrl)
      .then(() => {
        // Create and show a modal with more information
        const modal = document.createElement('div');
        modal.style.position = 'fixed';
        modal.style.top = '50%';
        modal.style.left = '50%';
        modal.style.transform = 'translate(-50%, -50%)';
        modal.style.backgroundColor = '#1e2329';
        modal.style.padding = '20px';
        modal.style.borderRadius = '8px';
        modal.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        modal.style.zIndex = '9999';
        modal.style.maxWidth = '400px';
        modal.style.width = '90%';
        modal.style.color = '#e0e0e0';
        
        modal.innerHTML = `
          <h3 style="margin-top: 0; color: #88a1ac;">Presence Link Created!</h3>
          <p>Share this link with others to let them view your workspace in real-time:</p>
          <div style="background: #151a1e; padding: 10px; border-radius: 4px; margin: 10px 0; word-break: break-all; font-size: 12px;">
            ${shareableUrl}
          </div>
          <p style="margin-bottom: 0;">✅ URL copied to clipboard</p>
          <p style="font-size: 12px; margin-top: 10px; color: #88a1ac;">When others open this link, they'll see your exact layout and their cursor will be visible to you.</p>
          <button id="close-modal" style="background: #88a1ac; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 15px; cursor: pointer; color: #151a1e;">Close</button>
        `;
        
        document.body.appendChild(modal);
        
        // Add event listener to close button
        document.getElementById('close-modal')?.addEventListener('click', () => {
          document.body.removeChild(modal);
        });
        
        // Auto-close after 10 seconds
        setTimeout(() => {
          if (document.body.contains(modal)) {
            document.body.removeChild(modal);
          }
        }, 10000);
      })
      .catch(err => console.error('Failed to copy URL:', err));
  }

  return (
    <button onClick={handlePresenceClick} className={styles.pluginButton}>
      Presence
    </button>
  )
}